import { SELECT_FIELD_TYPES, TEXT_FIELD_LIMITS, validationRuleKeys } from "../utils/leadFields";

export const EMPTY_FORM = {
  pipeline_id: "", key: "", label: "", description: "", section: "Additional information",
  field_type: "text", optionsText: "", default_value: "", is_required: false,
  is_searchable: true, is_filterable: true, is_table_visible: false, is_active: true,
  min: "", max: "", min_length: "", max_length: "", pattern: "",
};

export function keyFromLabel(label) {
  const key = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return (/^[0-9]/.test(key) ? `field_${key}` : key).slice(0, 100);
}

export function toForm(field) {
  let defaultValue = field.default_value ?? "";
  if (field.field_type === "checkbox" && defaultValue !== "") defaultValue = String(defaultValue);
  if (field.field_type === "datetime" && defaultValue) defaultValue = String(defaultValue).replace(" ", "T").slice(0, 19);

  return {
    ...EMPTY_FORM, ...field,
    pipeline_id: field.pipeline_id || "",
    optionsText: (field.options || []).map((option) => typeof option === "object" ? `${option.value}|${option.label}` : option).join("\n"),
    default_value: defaultValue,
    ...Object.fromEntries(validationRuleKeys(field.field_type).map((key) => [key, field.validation_rules?.[key] ?? ""])),
  };
}

export function changeFieldType(form, type) {
  if (form.field_type === type) return form;
  return { ...form, field_type: type, default_value: "", optionsText: "", min: "", max: "", min_length: "", max_length: "", pattern: "" };
}

export function parseFieldOptions(text) {
  return text.split(/\r?\n/).map((item) => item.trim()).filter(Boolean).map((item) => {
    const [value, ...label] = item.split("|");
    return label.length ? { value: value.trim(), label: label.join("|").trim() } : value.trim();
  });
}

export function payloadFromForm(form) {
  const rules = Object.fromEntries(validationRuleKeys(form.field_type)
    .filter((key) => form[key] !== "" && form[key] !== null && form[key] !== undefined)
    .map((key) => [key, key === "pattern" ? form[key] : Number(form[key])]));
  let defaultValue = form.default_value === "" ? null : form.default_value;
  if (form.field_type === "checkbox" && defaultValue !== null) defaultValue = String(defaultValue) === "true";
  if (form.field_type === "multi_select" && (!Array.isArray(defaultValue) || defaultValue.length === 0)) defaultValue = null;

  return {
    pipeline_id: form.pipeline_id || null, key: form.key, label: form.label.trim(),
    description: form.description || null, section: form.section || "Additional information",
    field_type: form.field_type, options: SELECT_FIELD_TYPES.has(form.field_type) ? parseFieldOptions(form.optionsText) : [],
    default_value: defaultValue, validation_rules: rules,
    is_required: form.is_required, is_searchable: form.is_searchable,
    is_filterable: form.is_filterable, is_table_visible: form.is_table_visible, is_active: form.is_active,
  };
}

export function validateFieldForm(form) {
  const errors = [];
  const rules = payloadFromForm(form).validation_rules;
  if (rules.min !== undefined && rules.max !== undefined && rules.min > rules.max) errors.push("Maximum value must be greater than or equal to minimum value.");
  if (rules.min_length !== undefined && rules.max_length !== undefined && rules.min_length > rules.max_length) errors.push("Maximum characters must be greater than or equal to minimum characters.");
  const limit = TEXT_FIELD_LIMITS[form.field_type];
  if (limit && (rules.min_length > limit || rules.max_length > limit)) errors.push(`This field type supports at most ${limit.toLocaleString()} characters.`);
  if (SELECT_FIELD_TYPES.has(form.field_type)) {
    const options = parseFieldOptions(form.optionsText);
    const values = options.map((option) => typeof option === "object" ? option.value : option);
    if (!values.length) errors.push("Add at least one option.");
    if (values.length > 100) errors.push("Use no more than 100 options.");
    if (values.some((value) => !value) || new Set(values).size !== values.length) errors.push("Each option needs a unique, non-empty value.");
    const defaults = Array.isArray(form.default_value) ? form.default_value : [form.default_value].filter((value) => value !== "");
    if (defaults.some((value) => !values.includes(value))) errors.push("Choose a default from the current options, or clear it.");
  }
  return errors;
}
